import type { ReactNode } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { tokens } from '../../theme/tokens';

type ScreenBackgroundVariant = 'page' | 'frame';

type ScreenBackgroundProps = {
  children: ReactNode;
  variant?: ScreenBackgroundVariant;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
};

type RidgeLine = {
  top: number;
  left: number;
  width: number;
  rotate: `${number}deg`;
  opacity: number;
};

type GrainDot = {
  top: number;
  left: number;
  size: number;
  opacity: number;
};

const ridgeLines: RidgeLine[] = [
  { top: 72, left: -48, width: 238, rotate: '-9deg', opacity: 0.3 },
  { top: 112, left: 148, width: 286, rotate: '7deg', opacity: 0.24 },
  { top: 184, left: -28, width: 210, rotate: '4deg', opacity: 0.2 },
  { top: 286, left: 196, width: 250, rotate: '-8deg', opacity: 0.18 },
  { top: 462, left: -88, width: 336, rotate: '8deg', opacity: 0.14 },
  { top: 642, left: 124, width: 300, rotate: '-6deg', opacity: 0.12 },
];

const grainDots: GrainDot[] = [
  { top: 54, left: 34, size: 2, opacity: 0.18 },
  { top: 156, left: 292, size: 3, opacity: 0.16 },
  { top: 246, left: 82, size: 2, opacity: 0.17 },
  { top: 338, left: 330, size: 2, opacity: 0.14 },
  { top: 438, left: 46, size: 3, opacity: 0.13 },
  { top: 552, left: 268, size: 2, opacity: 0.15 },
  { top: 696, left: 112, size: 2, opacity: 0.13 },
  { top: 760, left: 344, size: 3, opacity: 0.12 },
];

export function ScreenBackground({
  children,
  variant = 'page',
  style,
  contentStyle,
}: ScreenBackgroundProps) {
  const isFrame = variant === 'frame';

  return (
    <View style={[styles.root, isFrame ? styles.frameRoot : styles.pageRoot, style]}>
      <View pointerEvents="none" style={styles.textureLayer}>
        <View style={[styles.skyWash, isFrame ? styles.frameWash : styles.pageWash]} />
        {ridgeLines.map((line, index) => (
          <View
            key={`ridge-${index}`}
            style={[
              styles.ridgeLine,
              {
                top: line.top,
                left: line.left,
                width: line.width,
                opacity: line.opacity,
                transform: [{ rotate: line.rotate }],
              },
            ]}
          />
        ))}
        {grainDots.map((dot, index) => (
          <View
            key={`grain-${index}`}
            style={[
              styles.grainDot,
              {
                top: dot.top,
                left: dot.left,
                width: dot.size,
                height: dot.size,
                opacity: dot.opacity,
              },
            ]}
          />
        ))}
      </View>
      <View style={[styles.content, contentStyle]}>{children}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    overflow: 'hidden',
  },
  pageRoot: {
    backgroundColor: tokens.colors.background,
  },
  frameRoot: {
    backgroundColor: tokens.colors.appFrame,
  },
  textureLayer: {
    ...StyleSheet.absoluteFillObject,
  },
  skyWash: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 220,
    opacity: 0.56,
  },
  pageWash: {
    backgroundColor: tokens.colors.surfaceMuted,
  },
  frameWash: {
    backgroundColor: tokens.colors.background,
  },
  ridgeLine: {
    position: 'absolute',
    height: 2,
    borderRadius: tokens.radius.full,
    backgroundColor: tokens.colors.textMuted,
  },
  grainDot: {
    position: 'absolute',
    borderRadius: tokens.radius.full,
    backgroundColor: tokens.colors.textMuted,
  },
  content: {
    flex: 1,
  },
});
